import { useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  ErrorState,
  FailureNotice,
  FOCUS_RING,
  InlineConfirmation,
  LoadingState,
  Notice,
  Section,
  Textarea,
  cn,
  useLanguage,
  usePageTitle,
  useTranslate,
  CARD_SURFACE,
} from '@athena/ui'
import { Link } from '@tanstack/react-router'
import { useRef, useState } from 'react'

import { PlanTag } from '../features/display/Tags.js'
import { ListMessage } from '../features/management/ManagementSupport.js'
import {
  AdminDecideUpgradeRequestDocument,
  AdminUpgradeRequestsDocument,
} from '../graphql/generated/operations.js'
import { countOf, formatDate } from '../lib/display.js'
import { useFirstUserErrorText } from '../lib/user-errors.js'
import { useFocusFirstInvalid } from '../lib/validation.js'

/**
 * The review queue.
 *
 * This is the whole of Athena's "billing" in V1: a tutor asks, somebody
 * arranges payment outside the product, and an administrator records the
 * result here. Approving answers the request **and** applies the plan, in one
 * server call — an approval that only marked the row would be a tutor told yes
 * and left on Free.
 *
 * Either answer requires a note, and the form says so in words — under the
 * note, with focus moved to it — before the request goes out, as well as the
 * server enforcing it after. "No" with no reason is not an answer the tutor can
 * do anything with, and "yes" with none is a paid plan granted with nothing in
 * the audit log saying why (SECURITY_AUDIT.md SEC-016).
 *
 * Everything a decision needs is in the row: who asked, for which plan, when,
 * and what they wrote. Both answers are immediate — each is logged and either
 * can be followed by another plan change — so neither is asked twice.
 */
export function UpgradeRequestsScreen() {
  const t = useTranslate()
  const language = useLanguage()
  usePageTitle(t('admin.requestsTitle'))
  const userErrorText = useFirstUserErrorText()
  const [note, setNote] = useState<Record<string, string>>({})
  /** The one request answered without a note. */
  const [noteMissing, setNoteMissing] = useState<string | null>(null)
  const invalid = useFocusFirstInvalid<HTMLUListElement>()
  const [error, setError] = useState<string | null>(null)
  /**
   * The decision just confirmed, named, at the top of the list: its row leaves
   * the list with it, so the confirmation cannot sit beside it.
   */
  const [decided, setDecided] = useState<string | null>(null)
  /** A decision that never got an answer, with what it was, for the retry. */
  const [failure, setFailure] = useState<{ error: unknown; requestId: string; approve: boolean }>()
  /** The request being decided: only its buttons wait, and one press is one decision. */
  const [decidingId, setDecidingId] = useState<string | null>(null)
  const inFlight = useRef(false)

  const {
    data,
    loading,
    error: loadError,
  } = useQuery(AdminUpgradeRequestsDocument, {
    variables: { status: 'PENDING' },
  })
  const [decide] = useMutation(AdminDecideUpgradeRequestDocument, {
    refetchQueries: [AdminUpgradeRequestsDocument],
  })

  const requests = data?.adminUpgradeRequests
  return (
    <Section
      id="requests"
      level={1}
      title={t('admin.requestsTitle')}
      aside={
        requests && requests.length > 0 ? (
          <span className="text-body-sm text-text-secondary">
            {countOf(t, requests.length, 'admin.requestsCountOne', 'admin.requestsCountOther')}
          </span>
        ) : null
      }
    >
      <p className="max-w-prose text-body-sm text-text-secondary">{t('admin.requestsBody')}</p>

      {loading && !requests ? <LoadingState>{t('common.loading')}</LoadingState> : null}
      {loadError ? <ErrorState>{t('admin.loadError')}</ErrorState> : null}
      {error ? <Notice tone="error" title={error} /> : null}
      {failure ? (
        <FailureNotice
          title={t('admin.decisionNotMade')}
          error={failure.error}
          retrying={decidingId === failure.requestId}
          onRetry={() => void answer(failure.requestId, failure.approve, true)}
        />
      ) : null}
      {decided ? <InlineConfirmation>{decided}</InlineConfirmation> : null}

      {requests?.length === 0 ? <ListMessage>{t('admin.requestsEmpty')}</ListMessage> : null}

      {requests && requests.length > 0 ? (
        <ul ref={invalid.ref} className={cn(CARD_SURFACE, 'divide-y divide-border')}>
          {requests.map((request) => (
            <li
              key={request.id}
              className="grid gap-4 px-4 py-4 desktop:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] desktop:gap-6"
            >
              <div className="flex min-w-0 flex-col gap-2">
                <div className="min-w-0">
                  <p className="break-words text-body font-semibold text-text">
                    {request.tutorDisplayName || request.tutorEmail}
                  </p>
                  <p className="break-all text-body-sm text-text-secondary">
                    {request.tutorEmail} ·{' '}
                    <Link
                      to="/$language"
                      params={{ language }}
                      search={{ q: request.tutorEmail }}
                      className={cn(
                        'rounded-sm font-semibold text-primary underline-offset-2 hover:underline',
                        FOCUS_RING,
                      )}
                    >
                      {t('admin.findAccount')}
                    </Link>
                  </p>
                </div>
                <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-body-sm">
                  <div className="flex items-center gap-2">
                    <dt className="text-text-secondary">{t('admin.requestedPlan')}</dt>
                    <dd>
                      <PlanTag code={request.requestedPlanCode} />
                    </dd>
                  </div>
                  <div>
                    <dt className="sr-only">{t('admin.columnWhen')}</dt>
                    <dd className="text-text-secondary">
                      {t('admin.requestedOn', [formatDate(request.createdAt, language)])}
                    </dd>
                  </div>
                </dl>
                {request.message ? (
                  <figure className="border-l-2 border-border-strong pl-3">
                    <figcaption className="text-caption font-semibold text-text-secondary">
                      {t('admin.tutorMessage')}
                    </figcaption>
                    <blockquote className="break-words text-body-sm text-text">
                      {request.message}
                    </blockquote>
                  </figure>
                ) : null}
              </div>

              <div className="flex min-w-0 flex-col gap-3">
                <Textarea
                  label={t('admin.decisionNote')}
                  helperText={t('admin.decisionNoteHelper')}
                  rows={2}
                  value={note[request.id] ?? ''}
                  onChange={(event) => {
                    setNote((current) => ({ ...current, [request.id]: event.target.value }))
                    if (noteMissing === request.id) setNoteMissing(null)
                  }}
                  {...(noteMissing === request.id
                    ? { error: t('management.errors.decisionNote') }
                    : {})}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    className="max-md:h-touch-target-min"
                    loading={decidingId === request.id}
                    loadingText={t('common.saving')}
                    disabled={decidingId !== null && decidingId !== request.id}
                    onClick={() => answerWithNote(request.id, true)}
                  >
                    {t('admin.approve')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="max-md:h-touch-target-min"
                    disabled={decidingId !== null && decidingId !== request.id}
                    loading={decidingId === request.id}
                    loadingText={t('common.saving')}
                    onClick={() => answerWithNote(request.id, false)}
                  >
                    {t('admin.decline')}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  )

  /**
   * Decide one request. The note stays in its field whatever happens, so a
   * decision that did not go through can be sent again as written. Sending it
   * again is safe: a request that was in fact decided is refused in words.
   */
  /**
   * Required for either answer, here and on the server. The form says what is
   * missing; the server is the one that holds it.
   */
  function answerWithNote(requestId: string, approve: boolean) {
    if ((note[requestId] ?? '').trim() === '') {
      setNoteMissing(requestId)
      invalid.request()
      return
    }
    void answer(requestId, approve)
  }

  async function answer(requestId: string, approve: boolean, retry = false) {
    if (inFlight.current) return
    inFlight.current = true
    setDecidingId(requestId)
    setError(null)
    setDecided(null)
    if (!retry) setFailure(undefined)
    const request = data?.adminUpgradeRequests.find((row) => row.id === requestId)
    const text = (note[requestId] ?? '').trim()
    try {
      const result = await decide({
        variables: { requestId, approve, note: text },
        awaitRefetchQueries: true,
      })
      setFailure(undefined)
      const errors = result.data?.adminDecideUpgradeRequest.userErrors
      if (errors === undefined) {
        setFailure({
          error: new Error('The decision came back without a result.'),
          requestId,
          approve,
        })
      } else if (errors.length > 0) {
        setError(userErrorText(errors, t('common.tryAgain')))
      } else {
        setDecided(
          t(approve ? 'admin.requestApproved' : 'admin.requestDeclined', [
            request?.tutorEmail ?? '',
          ]),
        )
      }
    } catch (caught) {
      setFailure({ error: caught, requestId, approve })
    } finally {
      inFlight.current = false
      setDecidingId(null)
    }
  }
}
